import { Injectable } from '@angular/core';
import { FileItem, ZipFileEntry } from '../../../shared/models';
import { FileListService } from '../../../file-list/services/file-list.service';
import { LinkFileListService } from '../../../link-consume/services/link-file-list.service';
import { LoggerService } from '../../../core/logger.service';
import { AsyncSemaphore } from '../../../shared/helpers/async-semaphore';
import { sanitizePath } from '../../../shared/helpers/sanitize-path';
import { FOLDER_ENUMERATE_CONCURRENCY, FOLDER_ENUMERATE_MAX_DEPTH } from '../../transfer.constants';

export interface EnumerationResult {
  entries: ZipFileEntry[];
  failedPaths: string[];
}

export interface GroupedEnumerationResult {
  root: FileItem;
  entries: ZipFileEntry[];
  failedPaths: string[];
  folderCount: number;
}

export interface EnumerateOptions {
  concurrency?: number;
  maxDepth?: number;
  isCancelled?: () => boolean;
}

interface EnumerationContext {
  entries: ZipFileEntry[];
  failedPaths: string[];
  folderCount: number;
  semaphore: AsyncSemaphore;
  maxDepth: number;
  isCancelled: () => boolean;
}

@Injectable({
  providedIn: 'root',
})
export class FolderEnumeratorService {
  constructor(
    private fileListService: FileListService,
    private linkFileListService: LinkFileListService,
    private log: LoggerService,
  ) {}

  public async enumerateItems(items: FileItem[], opts: EnumerateOptions = {}): Promise<EnumerationResult> {
    const context = this.createContext(opts);

    const promises: Promise<void>[] = [];
    for (const item of items) {
      if (context.isCancelled()) {
        break;
      }
      if (item.type === 'dir') {
        promises.push(this.enumerateFolder(item, sanitizePath(item.name), 0, context));
      } else {
        context.entries.push({ file: item, relativePath: sanitizePath(item.name) });
      }
    }

    await Promise.all(promises);
    return { entries: context.entries, failedPaths: context.failedPaths };
  }

  public async enumerateItemsGrouped(
    items: FileItem[],
    opts: EnumerateOptions = {},
  ): Promise<GroupedEnumerationResult[]> {
    const semaphore = new AsyncSemaphore(opts.concurrency || FOLDER_ENUMERATE_CONCURRENCY);
    const maxDepth = opts.maxDepth || FOLDER_ENUMERATE_MAX_DEPTH;
    const isCancelled = opts.isCancelled || ((): boolean => false);

    const rootPromises = items.map(async (item): Promise<GroupedEnumerationResult> => {
      if (item.type !== 'dir') {
        return {
          root: item,
          entries: [{ file: item, relativePath: sanitizePath(item.name) }],
          failedPaths: [],
          folderCount: 0,
        };
      }
      const subContext: EnumerationContext = {
        entries: [],
        failedPaths: [],
        folderCount: 0,
        semaphore,
        maxDepth,
        isCancelled,
      };
      if (!isCancelled()) {
        await this.enumerateFolder(item, sanitizePath(item.name), 0, subContext);
      }
      return {
        root: item,
        entries: subContext.entries,
        failedPaths: subContext.failedPaths,
        folderCount: subContext.folderCount,
      };
    });

    return Promise.all(rootPromises);
  }

  private createContext(opts: EnumerateOptions): EnumerationContext {
    return {
      entries: [],
      failedPaths: [],
      folderCount: 0,
      semaphore: new AsyncSemaphore(opts.concurrency || FOLDER_ENUMERATE_CONCURRENCY),
      maxDepth: opts.maxDepth || FOLDER_ENUMERATE_MAX_DEPTH,
      isCancelled: opts.isCancelled || ((): boolean => false),
    };
  }

  private async enumerateFolder(
    folder: FileItem,
    currentPath: string,
    depth: number,
    context: EnumerationContext,
  ): Promise<void> {
    if (context.isCancelled()) {
      return;
    }

    if (depth >= context.maxDepth) {
      this.log.warn(`Skipping a folder at max depth (${context.maxDepth})`);
      return;
    }

    let children: FileItem[];

    await context.semaphore.acquire();
    try {
      if (context.isCancelled()) {
        return;
      }
      if (folder.context === 'link' && folder.linkID) {
        const linkData = await this.linkFileListService.getData(
          folder.share_key,
          folder.linkID,
          folder.sync_id,
          folder.linkpasswordlock || '',
        );
        children = linkData?.pathitems || [];
      } else {
        const result = await this.fileListService.getDataNoState(folder.sync_id, '');
        children = result.pathlist || [];
      }
      if (depth > 0) {
        context.folderCount++;
      }
    } catch {
      this.log.error(`Failed to enumerate a folder at depth ${depth}`);
      context.failedPaths.push(currentPath);
      return;
    } finally {
      context.semaphore.release();
    }

    if (children.length === 0 || context.isCancelled()) {
      return;
    }

    const promises: Promise<void>[] = [];
    for (const child of children) {
      if (context.isCancelled()) {
        break;
      }
      const sanitizedName = sanitizePath(child.name);
      const childPath = `${currentPath}/${sanitizedName}`;

      if (child.type === 'dir') {
        promises.push(this.enumerateFolder(child, childPath, depth + 1, context));
      } else {
        context.entries.push({ file: child, relativePath: childPath });
      }
    }

    await Promise.all(promises);
  }
}
